/* eslint-disable react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import { ButtonWithIcon } from '@els/els-react--button';
import { Flyout } from '@els/els-react--flyout';
import InnerHTML from '@Components/InnerHTML';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import SubstanceQuantitativeCell from '@Components/Results/substances/tables/quantitative/SubstanceQuantitativeCell';
import ReactTableColumnSwitcherHelper from '@Components/Results/services/react-table-columns-switcher-helper.service';
import { CITATION_LINKS_TRACKING_INDEX_MARKER } from '@Components/Results/analytics/citationLinksTracking';

import './SubstanceQuantitativeTable.scss';

// Only column-visibility rules — the show-more-* rules (padding-left/overflow/::before) are not
// needed because the show-more indicator is a real separate <th>/<td>, not a ::before overlay.
function generateVisibilityRules(tableId, headers) {
  return headers.map(({ cssid }) => `.${tableId}.show-${cssid} .column-${cssid} {display: table-cell;}`).join('');
}

function SubstanceQuantitativeTable({ metadata, data, uniqueId, showAllNonEmptyCols }) {
  const tableDescriptionId = `${uniqueId}-leyden-table-description`;
  const hiddenColumnsAmount = ReactTableColumnSwitcherHelper.getHiddenColumnsAmount(data.headers);
  const showMoreColumnClass = ReactTableColumnSwitcherHelper.getLastVisibleColumnClass(data.headers);
  const hiddenColumnsLabel = `Show ${hiddenColumnsAmount} ${hiddenColumnsAmount === 1 ? 'column' : 'columns'}`;

  return (
    <div className="substance-quantitative-table-container">
      <style>{generateVisibilityRules(uniqueId, data.headers)}</style>
      <table
        id={`${uniqueId}-leyden-table`}
        aria-label="Substance quantitative data"
        aria-describedby={tableDescriptionId}
        data-testid="table"
        className={`substance-quantitative-table ${ReactTableColumnSwitcherHelper.getTableClass(
          uniqueId,
          data.headers
        )} table c-els-table c-els-table--highlight-header c-els-table--vertical-dividers c-els-table--subtle-dividers`}>
        <caption className="c-els-table__caption u-els-hide-visually" id={tableDescriptionId}>
          Substance data table for the selected Identification section.
        </caption>
        <thead className="table__head c-els-table__head">
          <tr className="table__row c-els-table__row table__row--head c-els-table__row--head auto-resize substance-quantitative-table__head-row">
            {data.headers.map((header, headerIndex) => {
              const isShowMoreHeader = hiddenColumnsAmount > 0 && header.cssid === showMoreColumnClass;

              return (
                <React.Fragment key={header.id}>
                  {isShowMoreHeader && (
                    <th
                      scope="col"
                      className="table__cell c-els-table__cell table__cell--header c-els-table__cell--header substance-quantitative-table__show-more-col-header"
                      aria-label={hiddenColumnsLabel}>
                      <Flyout
                        className="substance-quantitative-table__show-more-flyout"
                        trigger="hover"
                        flyout={<span aria-hidden="true">{hiddenColumnsLabel}</span>}
                        placement="top"
                        theme="simple">
                        <ButtonWithIcon
                          data-e2e="table-header-show-hidden-columns"
                          type="link"
                          iconOnly
                          size="small"
                          sprite="Ellipsis"
                          aria-label={hiddenColumnsLabel}
                          onClick={showAllNonEmptyCols}>
                          {hiddenColumnsLabel}
                        </ButtonWithIcon>
                      </Flyout>
                    </th>
                  )}
                  <th
                    scope="col"
                    colSpan={header.colspan ? header.colspan : 1}
                    className={`table__cell c-els-table__cell c-els-table__basic-cell table__cell--header c-els-table__cell--header column-${
                      header.cssid
                    } ${header.id === 'paureus' ? 'px-header' : ''}`}
                    data-testid={`${header.id}-semantic-basic-cell-header`}
                    data-col={headerIndex}>
                    <span className="substance-quantitative-table__header-content">
                      <InnerHTML tag="span" html={header.title} />
                    </span>
                  </th>
                </React.Fragment>
              );
            })}
          </tr>
        </thead>
        <tbody className="table__body c-els-table__body">
          {data.rows.map((row, rowIndex) => (
            <tr
              key={`${uniqueId}-row-${rowIndex}`}
              id={`${uniqueId}-row-${rowIndex}`}
              data-testid={`${uniqueId}-row-${rowIndex}`}
              className="table__row c-els-table__row"
              {...CITATION_LINKS_TRACKING_INDEX_MARKER.substancesTableItem}>
              {data.headers.map((header, headerIndex) => {
                const isShowMoreCell = hiddenColumnsAmount > 0 && header.cssid === showMoreColumnClass;

                return (
                  <React.Fragment key={`${uniqueId}-row-${rowIndex}-${header.id}`}>
                    {isShowMoreCell && (
                      <td
                        className="table__cell c-els-table__cell c-els-table__element-cell substance-quantitative-table__show-more-col-cell"
                        onClick={showAllNonEmptyCols}
                        title={hiddenColumnsLabel}
                      />
                    )}
                    <SubstanceQuantitativeCell
                      metadata={metadata}
                      header={header}
                      data={row?.cells?.[headerIndex]?.value}
                      measurements={row?.cells?.[headerIndex]?.measurements}
                      rowIndex={rowIndex}
                      colIndex={headerIndex}
                    />
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

SubstanceQuantitativeTable.propTypes = {
  metadata: MetadataPropTypesShape,
  data: PropTypes.object.isRequired,
  uniqueId: PropTypes.string.isRequired,
  showAllNonEmptyCols: PropTypes.func.isRequired,
};

export default SubstanceQuantitativeTable;
